import { useCallback } from "react";
import "@deere/gdl-widget-2_0/dist/widget.css";
import GDLWidgetAppProd from "@deere/gdl-widget-2_0/dist/gdl-widget-2_0";

/**
 * Reproduces the deere.com Construction Products PDP layout where multiple
 * GDLWidget integrations live on the same page and share one Redux store:
 *   - Header (industry flow, no agreementCodes)
 *   - Body / primary (industry flow)
 *   - PRODUCT (agreementCodes flow)  <-- the one that was being overridden
 *   - Footer (industry flow)
 *
 * Use this page to verify that dispatches from the industry widgets do NOT
 * overwrite the product widget's agreement-code dealers.
 */
function MultiWidgetTest() {
    // ---- EDIT THESE for your environment ----
    // eslint-disable-next-line
    const COUNTRY = "US";
    // eslint-disable-next-line
    const ADDRESS = "Moline, IL, USA";
    // eslint-disable-next-line
    const AGREEMENT_CODES = ["AH",

        "LD",

        "LW"];
    // eslint-disable-next-line 
    const PRODUCT_GROUP_ID = 2; 
    // -----------------------------------------

    // The widget's bundle invokes props.getDealerData(...) directly, so we must
    // pass a function. Use a stable no-op so nothing is rendered/printed.
    const noop = useCallback(() => {}, []);

    const sectionStyle = {
        padding: 16,
        margin: "12px 0",
        border: "1px dashed #999",
        borderRadius: 6,
    };
    const productStyle = {
        ...sectionStyle,
        border: "2px solid #367c2b",
        background: "#f5fff2",
    };

    return (
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: 16, fontFamily: "Arial, sans-serif" }}>
            <h1>Multi-Widget Integration Test</h1>
            <p>
                This page mounts <strong>4 GDLWidget instances</strong> sharing a single Redux store, mirroring the
                deere.com PDP layout. The <span style={{ color: "#367c2b", fontWeight: 700 }}>green</span> widget
                uses <code>agreementCodes</code>; the others use industry. With the fix, the green widget's dealers
                must remain unchanged after the industry widgets fire their fetches.
            </p>
            <p>
                Open Redux DevTools and watch <code>state.data.dealers</code> vs{" "}
                <code>state.data.dealersByAgreementCode</code> &mdash; they should populate independently.
            </p>

            {/* Header */}
            <div style={sectionStyle}>
                <h3>1. Header widget (industry)</h3>
                <GDLWidgetAppProd
                    key="multi-header"
                    variant="tertiary"
                    buttonText="Find a Dealer (header)"
                    // countryCode={COUNTRY}
                    // address={ADDRESS}
                    // productGroupId={PRODUCT_GROUP_ID}
                    isCoreMediaEnabled={true}
                    experience="brand"
                    gdlButtonColor="onLight"
                    getDealerData={noop}
                />
            </div>

            {/* Body / primary */}
            <div style={sectionStyle}>
                <h3>2. Body widget (industry, primary)</h3>
                <GDLWidgetAppProd
                    key="multi-body"
                    variant="primary"
                    buttonText="Find a Dealer (body)"
                    // countryCode={COUNTRY}
                    // address={ADDRESS}
                    // productGroupId={PRODUCT_GROUP_ID}
                    isCoreMediaEnabled={true}
                    experience="brand"
                    gdlButtonColor="onLight"
                    getDealerData={noop}
                />
            </div>

            {/* PRODUCT — the one with agreementCodes */}
            <div style={productStyle}>
                <h3>3. Product widget (agreementCodes) &mdash; should NOT be overridden</h3>
                <GDLWidgetAppProd
                    key="multi-product"
                    variant="primary"
                    buttonText="Find a Dealer (product)"
                    // countryCode={COUNTRY}
                    // address={ADDRESS}
                    agreementCodes={AGREEMENT_CODES}
                    productName="Z315E"
                    isCoreMediaEnabled={true}
                    experience="brand"
                    gdlButtonColor="onLight"
                    getDealerData={noop}
                />
            </div>

            {/* Footer */}
            <div style={sectionStyle}>
                <h3>4. Footer widget (industry)</h3>
                <GDLWidgetAppProd
                    key="multi-footer"
                    variant="tertiary"
                    buttonText="Find a Dealer (footer)"
                    // countryCode={COUNTRY}
                    // address={ADDRESS}
                    // productGroupId={PRODUCT_GROUP_ID}
                    isCoreMediaEnabled={true}
                    experience="brand"
                    gdlButtonColor="onDark"
                    getDealerData={noop}
                />
            </div>

            <h2>How to verify</h2>
            <ol>
                <li>Open the page; all four buttons should render.</li>
                <li>
                    DevTools &rarr; Network: filter by <code>dealers</code>. You should see calls to{" "}
                    <code>dealersByIndustry</code> (header/body/footer) and{" "}
                    <code>dealersByAgreementCode</code> (product).
                </li>
                <li>
                    Click the product widget &mdash; the drawer should show agreement-code dealers. Then click the
                    body/header widget &mdash; their drawers should show industry dealers. Re-open the product widget:
                    its dealers must still be the agreement-code ones.
                </li>
                <li>
                    Redux DevTools: <code>state.data.dealers</code> changes only for industry widgets;{" "}
                    <code>state.data.dealersByAgreementCode</code> changes only for the product widget.
                </li>
                <li>
                    To force the race, throttle the network to <em>Slow 3G</em> in DevTools and click multiple
                    widgets quickly.
                </li>
            </ol>
        </div>
    );
}

export default MultiWidgetTest;
